import React from "react";

// -----------------------------------------------------------------------------
// License Agreement & Statement of Purpose modal.
//
// 2026-02-27 (Dave) — Combines two pieces the user must acknowledge on first
// login (or after material updates):
//
//   * Statement of Purpose — big-picture framing of what uReserve is and,
//     critically, is NOT (with the ALL-CAPS "users remain solely
//     responsible" clause preserved verbatim from the 2026-02-15 draft).
//   * End User License Agreement — five numbered clauses covering license
//     grant, purpose, data, liability, and governing law.
//
// Layout goal: everything fits in one viewport at 1080p / 900p — the SOP
// paragraph anchors the top; the five EULA clauses are arranged in a
// 3×2 grid below so the whole modal stays under ~700 px tall. Modal is
// wider (1080 px cap) than the previous 720 px version to accommodate
// the grid without cramping.
// -----------------------------------------------------------------------------

// Kept as data (rather than JSX) so a future admin CRUD screen can move it
// to the DB without touching layout code.
export const SOP_PARAGRAPH_PARTS = [
  { t: "uReserve is not a reserve study, as defined by CAI or APRA standards, an engineering report, legal opinion, accounting opinion, investment advisory service, or guarantee of future reserve adequacy. " },
  { t: "USERS, BOARDS OF DIRECTORS, AND PROFESSIONAL ADVISORS REMAIN SOLELY RESPONSIBLE FOR VALIDATING ASSUMPTIONS, EXERCISING INDEPENDENT JUDGMENT, AND MAKING FINAL FUNDING AND CAPITAL PLANNING DECISIONS.", bold: true },
  { t: " All projections are based upon user-provided information and assumptions, and actual results may differ materially. uReserve\u00AE is an interactive reserve planning and decision-support platform designed to help community association boards, property managers, reserve advisors, and other stakeholders evaluate long-term reserve funding strategies through modeling, forecasting, scenario analysis, and visualization tools. It is intended solely to support planning, education, collaboration, and informed decision-making." },
];

// Back-compat named export — some tests import SOP_TEXT_PARAGRAPHS by name.
export const SOP_TEXT_PARAGRAPHS = [{ parts: SOP_PARAGRAPH_PARTS }];

const EULA_CLAUSES = [
  {
    n: 1,
    title: "License and Ownership",
    body: "uReserve Planning, LLC (\u201CuReserve\u201D) gives you a limited, revocable, non-transferable right to use uReserve while you are an authorized user of an organization with an active subscription. uReserve and its software, calculation methods and engine, formulas, databases, workflows, reports, designs, documentation, trademarks, and other intellectual property remain the property of uReserve. You may not copy, sell, sublicense, reverse engineer, reproduce, attempt to discover source code or proprietary methods, circumvent security or licensing controls, or use uReserve or its methods to develop a competing product.",
  },
  {
    n: 2,
    title: "Purpose and User Responsibility",
    body: "uReserve is a reserve-planning and decision-support tool. Its calculations and projections depend on information, estimates, assumptions, and future events that may change or prove incorrect. uReserve does not guarantee reserve adequacy or future results and does not replace inspections, reserve studies, or professional engineering, legal, accounting, tax, or other advice. You are responsible for the information entered, assumptions selected, review and use of results, compliance with applicable laws, and obtaining professional advice when appropriate.",
  },
  {
    n: 3,
    title: "Data and Service",
    body: "You retain ownership of data you provide. You authorize uReserve to process and store that data as needed to provide the service. uReserve may use aggregated or anonymized information to operate and improve its services and may update or modify the software from time to time.",
  },
  {
    n: 4,
    title: "Disclaimer and Liability",
    body: "uReserve is provided \u201CAS IS\u201D and \u201CAS AVAILABLE.\u201D To the fullest extent permitted by law, uReserve disclaims all express and implied warranties and is not liable for indirect, incidental, special, consequential, or similar damages, including lost profits, business interruption, or lost data. To the fullest extent permitted by law, uReserve\u2019s total liability for any claim related to the software will not exceed the subscription fees paid for the software during the 12 months before the event giving rise to the claim.",
  },
  {
    n: 5,
    title: "Access, Changes and Law",
    body: "Your right to use uReserve ends when your authorization or applicable subscription ends or if you violate this Agreement. uReserve may update this Agreement, and material changes may require you to review and agree again. This Agreement is governed by Minnesota law, without regard to conflict-of-law rules.",
  },
];

// Modal + tinted halo backdrop. Blocks all interaction with the page beneath
// until the user clicks the acknowledge button.
//
// Props unchanged from the pre-merge SOP modal so App.js keeps working:
//   open · onAcknowledge · errorMessage · onSignOut
// Optional (subscription re-acknowledgement): acknowledgeLabel · onCancel · intro
export default function StatementOfPurposeModal({
  open, onAcknowledge, errorMessage, onSignOut,
  acknowledgeLabel = "Agree & Continue", onCancel, intro, busy = false,
}) {
  if (!open) return null;

  return (
    <div
      // Tinted halo backdrop — navy tint at low opacity + blur so the Dashboard
      // stays visible but clearly out-of-reach.
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{
        backgroundColor: "rgba(15, 42, 68, 0.28)",
        backdropFilter: "blur(1.5px)",
        WebkitBackdropFilter: "blur(1.5px)",
      }}
      data-testid="sop-modal-backdrop"
    >
      <div
        className="bg-white shadow-2xl border border-rule w-[min(1080px,96vw)] max-h-[92vh] flex flex-col rounded-[8px]"
        data-testid="sop-modal"
      >
        {/* Header */}
        <div
          className="px-6 py-4"
          style={{ borderBottom: "2px solid #1C4E80", background: "#F7FAFC" }}
        >
          <div
            className="text-[15px] font-bold text-navy tracking-[0.02em]"
            data-testid="sop-modal-title"
          >
            License Agreement &amp; Statement of Purpose
          </div>
          <div className="text-[11.5px] text-slate mt-0.5" data-testid="sop-modal-intro">
            {intro || "Please review and acknowledge before using uReserve"}<sup>&reg;</sup>. By
            selecting <span className="italic">{acknowledgeLabel}</span> you
            confirm you have read, understand, and agree to this Agreement and
            to the uReserve Terms of Service and Privacy Policy.
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-auto text-ink">
          {/* Statement of Purpose — full width, sits above the numbered
              license clauses so it establishes context first. */}
          <div className="mb-3">
            <div
              className="text-[10.5px] tracking-[0.12em] uppercase font-bold text-navy mb-1 pb-1"
              style={{ borderBottom: "1px solid #EA6A47" }}
            >
              Statement of Purpose
            </div>
            <p className="m-0 text-[11.5px] leading-[1.55]" style={{ textAlign: "justify" }}>
              {SOP_PARAGRAPH_PARTS.map((seg, i) => (
                <span key={i} style={seg.bold ? { fontWeight: 700 } : undefined}>
                  {seg.t}
                </span>
              ))}
            </p>
          </div>

          {/* End User License Agreement — five clauses in a 3×2 grid so the
              whole modal fits without scrolling. Each cell has the same
              coral-rule heading treatment used across the app for section
              titles inside cards. */}
          <div
            className="text-[10.5px] tracking-[0.12em] uppercase font-bold text-navy mb-2 pb-1"
            style={{ borderBottom: "1px solid #EA6A47" }}
          >
            End User License Agreement
          </div>
          <div className="grid grid-cols-3 gap-x-4 gap-y-3">
            {EULA_CLAUSES.map((c) => (
              <div key={c.n} className="text-[11px] leading-[1.5]">
                <div className="text-[10.5px] font-bold text-navy mb-0.5 tracking-[0.02em]">
                  {c.n}. {c.title}
                </div>
                <div className="text-ink" style={{ textAlign: "justify" }}>
                  {c.body}
                </div>
              </div>
            ))}
            {/* Sixth slot in the 3×2 grid — closing acknowledgment sentence
                so the visual grid stays balanced and the acknowledgment
                sits inline with the clauses it summarizes. */}
            <div className="text-[11px] leading-[1.5] bg-[#F7FAFC] border-l-2 border-navy pl-2 pr-1 py-1">
              <div className="text-[10.5px] font-bold text-navy mb-0.5 tracking-[0.02em]">
                Acknowledgment
              </div>
              <div className="text-ink" style={{ textAlign: "justify" }}>
                By selecting <span className="italic">{acknowledgeLabel}</span>,
                you acknowledge that you have read and understand this
                Agreement, agree to be bound by it, and, when acting for an
                organization, confirm that you are authorized to do so.
              </div>
            </div>
          </div>
        </div>

        {errorMessage && (
          <div
            className="mx-6 mb-2 px-2.5 py-2 border border-coral bg-[#FEF5F2] text-coral text-[11px] leading-snug"
            data-testid="sop-modal-error"
          >
            {errorMessage}
          </div>
        )}

        {/* Footer */}
        <div
          className="px-6 py-3 flex items-center justify-between"
          style={{ borderTop: "1px solid #E4E7EB", background: "#F7FAFC" }}
        >
          {onCancel ? (
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="text-[13px] font-semibold text-navy border border-navy px-5 py-2 rounded-[3px] hover:bg-[#F2F9FD] transition-colors disabled:opacity-50"
              data-testid="sop-cancel-btn"
            >
              Cancel
            </button>
          ) : onSignOut ? (
            <button
              type="button"
              onClick={onSignOut}
              className="text-[11px] text-slate hover:text-navy underline"
              data-testid="sop-signout-btn"
            >
              Sign out instead
            </button>
          ) : <span />}
          <button
            onClick={onAcknowledge}
            disabled={busy}
            className="bg-navy hover:bg-[#12406B] text-white text-[13px] font-semibold px-5 py-2.5 rounded-[3px] transition-colors disabled:opacity-60"
            data-testid="sop-acknowledge-btn"
            autoFocus
          >
            {acknowledgeLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
